import {
  calculateHeartRatePercentageOfMax, calculateHeartRateZoneOnly,
  calculateMetrics, getTimeInWorkout, HomeWorkoutSequence,
} from 'fitbeat.gym-core';
import { HeartRateStep, IHeartRate, IMetricResult } from 'fitbeat.models';
import produce from 'immer';
import { ThemeType } from '../theme';
import { CLOSE_HOME_WORKOUT, COMPLETE_WORKOUT_SUCCESS, OPEN_HOME_WORKOUT_REQUEST } from './actions';
import { StudioHRMSetupScreen } from './components/HeartRateMonitorSetupStudio';
import {
  ADD_HEART_RATE_DEVICE_TO_DISCOVER_LIST,
  CLEAR_HEART_RATE_DEVICE_LIST,
  DISCONNECT_HEART_RATE_MONITOR,
  GO_TO_HR_SETUP_STEP,
  HEART_RATE_DEVICE_DISCOVER_ERROR,
  HEART_RATE_MONITOR_CHANGED,
  HEART_RATE_RECEIVED_FAILURE,
  HEART_RATE_RECEIVED_SUCCESS,
  HEART_RATE_SCAN_SUCCESS,
  HEART_RATE_SET_TESTING_STUB,
  OPEN_STUDIO_HRM_INSTRUCTIONS_BUTTON_PRESS,
  REMOVE_HEART_RATE_MONITOR_IDENTIFIER,
  REMOVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS,
  SAVE_HEART_RATE_MONITOR_IDENTIFIER,
  SAVE_HEART_RATE_MONITOR_IDENTIFIER_FAILURE,
  SAVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS,
  SET_FAQ_STATE,
  SET_THEME_TYPE,
  START_BELT_SCAN,
  START_HEART_RATE_DEVICE_DISCOVERY,
  START_WATCH_APP_WORKOUT_REQUEST,
  START_WATCH_APP_WORKOUT_REQUEST_FAILURE,
  START_WATCH_APP_WORKOUT_TIMER_COMPLETED,
  STOP_BELT_SCAN,
  STUDIO_HRM_INSTRUCTIONS_BACK_BUTTON_PRESS,
  SUPERFLUOUS_CONNECTED_APP_HEART_RATE_DATA,
  VERIFY_METRIC_DATA_IS_NOT_STALE,
} from './heartRateActions';
import { BluetoothService } from './utils';
import { BluetoothTimeoutError } from './utils/BluetoothTimeoutError';
import { HeartRateTempPersistance } from './HeartRateTempPersistance';
import { Logger } from '../utils';

export const heartRateTempPersistance = new HeartRateTempPersistance(Logger);

export interface IHeartRateBeltStoreState {
  isScanningBelt: boolean;
  metricResult: IMetricResult | null | Partial<IMetricResult> | undefined;
  latestLiveHeartRate: number | null;
  latestLiveHeartRateSourceType: 'ConnectedApp' | 'Bluetooth' | null;
  latestConnectedAppHR: IHeartRate | null;
  heartRateDataPoints: IHeartRate[];
  heartRateRecievedError: string | null;
  metricsForWorkoutId: string | null;
  lastMetricCalculatedTimestamp: Date | null;
  metricDataHasGoneStale: boolean;
  heartRateUseTestingStub: boolean;
  heartRateDevices: any[];
  heartRateDiscoverDidTimeout: boolean;
  heartRateDiscoverError: string | null;
  isSavingHeartRateIdentifier: boolean;
  savingHeartRateIdentifierFailure: string | null;
  isRemovingHeartRateIdentifier: boolean;
  calculatedFinalResult: boolean;
  heartRateSetupStep: HeartRateStep | null;
  themeType: ThemeType;
  faqState: any;
  heartRateMonitorDisconnected: boolean;
  studioHRMSetupScreen: StudioHRMSetupScreen;
  waitingForWatchHRData: boolean | number;
  failedToReceiveHRFromConnectedApp: boolean;
  failedToReceiveHRFromConnectedAppError: 'Timed out' | string | null;
  userHasConnectedAppForHeartRate: boolean;
}

export const initialState: IHeartRateBeltStoreState = {
  isScanningBelt: false,
  metricResult: null,
  heartRateDataPoints: [],
  heartRateRecievedError: null,
  metricsForWorkoutId: null,
  lastMetricCalculatedTimestamp: null,
  metricDataHasGoneStale: false,
  heartRateUseTestingStub: false,
  heartRateDevices: [],
  heartRateDiscoverDidTimeout: false,
  heartRateDiscoverError: null,
  isSavingHeartRateIdentifier: false,
  isRemovingHeartRateIdentifier: false,
  calculatedFinalResult: false,
  savingHeartRateIdentifierFailure: '',
  heartRateSetupStep: HeartRateStep.TEST_BELT,
  themeType: ThemeType.light,
  faqState: {
    isActive: false,
    stepBeforeFaq: null,
  },
  heartRateMonitorDisconnected: false,
  latestLiveHeartRate: null,
  latestLiveHeartRateSourceType: null,
  latestConnectedAppHR: null,
  studioHRMSetupScreen: 'CaptureHRMId',
  waitingForWatchHRData: false,
  failedToReceiveHRFromConnectedApp: false,
  failedToReceiveHRFromConnectedAppError: null,
  userHasConnectedAppForHeartRate: false,
};

export const HeartRateMonitor = produce(
  (draft: IHeartRateBeltStoreState = initialState, action: any): IHeartRateBeltStoreState => {
    switch (action.type) {
      case OPEN_HOME_WORKOUT_REQUEST:
        return initialState;
        break;
      case COMPLETE_WORKOUT_SUCCESS:
        heartRateTempPersistance.deleteAllStoredHeartRates();
        break;
      case CLOSE_HOME_WORKOUT:
        heartRateTempPersistance.deleteAllStoredHeartRates();
        return initialState;
        break;
      case SAVE_HEART_RATE_MONITOR_IDENTIFIER:
        draft.isSavingHeartRateIdentifier = true;
        draft.savingHeartRateIdentifierFailure = null;
        break;
      case SAVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS:
        draft.isSavingHeartRateIdentifier = false;
        draft.heartRateSetupStep = HeartRateStep.TEST_BELT;
        draft.savingHeartRateIdentifierFailure = null;
        break;
      case SAVE_HEART_RATE_MONITOR_IDENTIFIER_FAILURE:
        const e = action.payload;
        draft.isSavingHeartRateIdentifier = false;
        draft.savingHeartRateIdentifierFailure = e.message || 'Failed to save Heart Rate Monitor';
        break;
      case HEART_RATE_SET_TESTING_STUB:
        const useTestingStub = action.payload.useTestingStub;
        draft.heartRateUseTestingStub = useTestingStub;
        break;
      case ADD_HEART_RATE_DEVICE_TO_DISCOVER_LIST:
        const device = action.payload;
        const compoundKey = `${device.id}_${device.name}_${device.localName}_${device.manufacturerData}`;
        const index = draft.heartRateDevices.findIndex((x) => x.id === device.id);
        if (index === -1) {
          const hrDevice = {
            ...device,
            name: determineDeviceNameFromLocalname(device),
            maskedLocalName: maskLocalName(device),
            compoundKey,
          };
          draft.heartRateDevices.push(hrDevice);
        }
        draft.heartRateDiscoverDidTimeout = false;
        draft.heartRateDiscoverError = null;
        break;
      case HEART_RATE_DEVICE_DISCOVER_ERROR:
        const error = action.payload.error;
        if (error instanceof BluetoothTimeoutError) {
          draft.heartRateDiscoverDidTimeout = true;
          draft.heartRateDevices.length === 0
            ? draft.heartRateDiscoverError = 'noDevicesFound' : draft.heartRateDiscoverError = 'noMoreDevicesFound';
        } else {
          draft.heartRateDiscoverError = error.message;
        }

        break;
      case START_HEART_RATE_DEVICE_DISCOVERY:
        draft.heartRateDevices = [];
        draft.heartRateDiscoverDidTimeout = false;
        draft.heartRateDiscoverError = null;
        draft.heartRateMonitorDisconnected = false;
        draft.latestLiveHeartRate = null;
        draft.latestLiveHeartRateSourceType = null;
        break;
      case CLEAR_HEART_RATE_DEVICE_LIST:
        draft.heartRateDevices = [];
        break;
      case REMOVE_HEART_RATE_MONITOR_IDENTIFIER:
        draft.isRemovingHeartRateIdentifier = true;
        break;
      case REMOVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS:
        draft.isRemovingHeartRateIdentifier = false;
        draft.heartRateDiscoverDidTimeout = false;
        draft.heartRateDiscoverError = null;
        draft.heartRateDevices = [];
        draft.heartRateSetupStep = HeartRateStep.CAPTURE_BELT;
        draft.heartRateMonitorDisconnected = false;
        break;
      case DISCONNECT_HEART_RATE_MONITOR:
        draft.heartRateMonitorDisconnected = true;
        draft.isScanningBelt = false;
        draft.heartRateRecievedError = null;
        draft.heartRateDiscoverDidTimeout = false;
        draft.heartRateDiscoverError = null;
        draft.latestLiveHeartRate = null;
        draft.latestLiveHeartRateSourceType = null;
        draft.metricResult = {
          ...draft.metricResult,
          latestHeartRate: undefined,
          latestHeartRateZone: undefined,
          latestEffort: undefined,
        };
        break;
      case SET_THEME_TYPE:
        draft.themeType = action.payload.themeType;
        break;
      case SET_FAQ_STATE:
        draft.faqState = action.payload;
        break;
      case GO_TO_HR_SETUP_STEP:
        const setupStep = action.payload.step;
        draft.heartRateSetupStep = setupStep;
        break;
      case START_BELT_SCAN:
        const { workoutId } = action.payload;
        if (!workoutId || workoutId !== draft.metricsForWorkoutId) {
          draft.lastMetricCalculatedTimestamp = null;
        }
        draft.isScanningBelt = true;
        draft.heartRateRecievedError = null;
        draft.metricsForWorkoutId = workoutId;
        draft.latestLiveHeartRate = null;
        draft.latestLiveHeartRateSourceType = null;
        draft.heartRateMonitorDisconnected = false;
        break;
      case STOP_BELT_SCAN:
        draft.isScanningBelt = false;
        draft.heartRateRecievedError = null;
        draft.heartRateMonitorDisconnected = false;
        break;
      case HEART_RATE_SCAN_SUCCESS:
        const hrData = action.payload.heartRateData;
        draft.latestLiveHeartRate = hrData.heartRate;
        if (hrData.source && !hrData.source.startsWith(BluetoothService.SOURCE_PREFIX)) {
          draft.latestConnectedAppHR = hrData;
          draft.latestLiveHeartRateSourceType = 'ConnectedApp';
        } else {
          draft.latestLiveHeartRateSourceType = 'Bluetooth';
        }
        draft.isScanningBelt = false;
        draft.heartRateRecievedError = null;
        draft.heartRateMonitorDisconnected = false;
        break;
      case SUPERFLUOUS_CONNECTED_APP_HEART_RATE_DATA:
        draft.latestConnectedAppHR = action.payload.heartRateData;
        break;
      case HEART_RATE_RECEIVED_SUCCESS:
        const {
          heartRateData,
          bodyDetails,
          homeWorkoutSession,
          workoutPauseStartTime,
          isPastWarmdown,
        } = action.payload;
        const pushMetrics = shouldPushMetrics(
          homeWorkoutSession,
          heartRateData.timestamp,
          !!workoutPauseStartTime,
          isPastWarmdown,
        );
        draft.latestLiveHeartRate = heartRateData.heartRate;
        if (heartRateData.source && !heartRateData.source.startsWith(BluetoothService.SOURCE_PREFIX)) {
          draft.latestConnectedAppHR = heartRateData;
          draft.waitingForWatchHRData = false;
          draft.latestLiveHeartRateSourceType = 'ConnectedApp';
        } else {
          draft.latestLiveHeartRateSourceType = 'Bluetooth';
        }
        if (pushMetrics) {
          hydratePreviousDataIfNecessary(draft, homeWorkoutSession.workoutId);
          const heartRateDataPoint = buildHeartRateData(heartRateData, homeWorkoutSession);
          draft.heartRateDataPoints.push(heartRateDataPoint);
          heartRateTempPersistance.storeHeartRate(heartRateDataPoint, homeWorkoutSession.workoutId);
        }

        let metricResult = draft.metricResult;
        draft.calculatedFinalResult = !draft.calculatedFinalResult && isPastWarmdown;
        if (shouldCalculateMetrics(
          draft.lastMetricCalculatedTimestamp,
          !!workoutPauseStartTime,
          isPastWarmdown,
          draft.calculatedFinalResult,
        )) {
          const cardioOrResistance = homeWorkoutSession.workoutSettings.cardioOrResistance;
          metricResult = calculateMetrics(bodyDetails, draft.heartRateDataPoints, cardioOrResistance);
          draft.lastMetricCalculatedTimestamp = new Date();
        }
        draft.metricResult = {
          ...metricResult,
          latestHeartRate: heartRateData.heartRate,
          latestHeartRateZone: calculateHeartRateZoneOnly(heartRateData.heartRate, bodyDetails),
          latestEffort: calculateHeartRatePercentageOfMax(heartRateData.heartRate, bodyDetails),
        };

        draft.isScanningBelt = false;
        draft.heartRateRecievedError = null;
        draft.heartRateMonitorDisconnected = false;
        break;
      case HEART_RATE_RECEIVED_FAILURE:
        draft.isScanningBelt = false;
        draft.heartRateRecievedError = action.payload;
        break;
      case VERIFY_METRIC_DATA_IS_NOT_STALE:
        const lastReceivedMetric = draft.heartRateDataPoints[draft.heartRateDataPoints.length - 1];
        if (!lastReceivedMetric) {
          draft.metricDataHasGoneStale = false;
          break;
        }
        if (!action.payload.homeWorkoutSession) {
          break;
        }

        const timeInWorkout = getTimeInWorkout(
          action.payload.homeWorkoutSession,
          action.payload.now,
        );
        const differenceInMetricResult = timeInWorkout.getTime() - lastReceivedMetric.timestamp.getTime();
        const timeToStale = draft.latestLiveHeartRateSourceType === 'Bluetooth' ? 15000 : 60000;
        draft.metricDataHasGoneStale = differenceInMetricResult > timeToStale;
        break;
      case HEART_RATE_MONITOR_CHANGED:
        draft.lastMetricCalculatedTimestamp = null;
        draft.metricDataHasGoneStale = false;
        draft.calculatedFinalResult = false;
        draft.latestLiveHeartRate = null;
        draft.latestLiveHeartRateSourceType = null;
        break;
      case OPEN_STUDIO_HRM_INSTRUCTIONS_BUTTON_PRESS:
        draft.studioHRMSetupScreen = 'StudioHRMInstructions';
        break;
      case STUDIO_HRM_INSTRUCTIONS_BACK_BUTTON_PRESS:
        draft.studioHRMSetupScreen = 'CaptureHRMId';
        break;
      case 'ui/modal/heartRateMonitorModalStudioVisible':
        draft.studioHRMSetupScreen = 'CaptureHRMId';
        break;
      case START_WATCH_APP_WORKOUT_REQUEST:
        draft.userHasConnectedAppForHeartRate = true;
        draft.waitingForWatchHRData = action.payload.requestId;
        draft.failedToReceiveHRFromConnectedApp = false;
        draft.failedToReceiveHRFromConnectedAppError = null;
        break;
      case START_WATCH_APP_WORKOUT_REQUEST_FAILURE:
        draft.waitingForWatchHRData = false;
        draft.failedToReceiveHRFromConnectedApp = true;
        draft.failedToReceiveHRFromConnectedAppError = action.payload.error;
        break;
      case START_WATCH_APP_WORKOUT_TIMER_COMPLETED:
        if (draft.waitingForWatchHRData !== action.payload.requestId) {
          Logger.leaveBreadcrumb('Ignoring watch app timeout error from a previous request', {
            currentRequest: draft.waitingForWatchHRData,
            timedoutRequestId: action.payload.requestId,
          });
          return draft;
        }

        draft.waitingForWatchHRData = false;
        const hasAlreadyFailed = draft.failedToReceiveHRFromConnectedApp;
        if (!hasAlreadyFailed) {
          draft.failedToReceiveHRFromConnectedApp = !draft.latestConnectedAppHR;
          if (draft.failedToReceiveHRFromConnectedApp) {
            draft.failedToReceiveHRFromConnectedAppError = 'Timed out';
          } else {
            draft.failedToReceiveHRFromConnectedAppError = null;
          }
        }

        break;
    }
    return draft;
  },
);

const shouldPushMetrics = (
  homeWorkoutSession: HomeWorkoutSequence,
  metricTimestamp: Date,
  workoutIsPaused: boolean,
  isPastWarmdown: boolean,
): boolean => {
  const metricTimeStamp = metricTimestamp.getTime();
  return metricTimeStamp >= homeWorkoutSession.startTimeOfFirstExercise.getTime()
    && metricTimeStamp <= homeWorkoutSession.endTime.getTime() && !workoutIsPaused && !isPastWarmdown;
};

const shouldCalculateMetrics = (
  lastMetricCalculatedTimestamp: Date | null,
  workoutIsPaused: boolean,
  isPastWarmdown: boolean,
  shouldCalculateFinalResult: boolean,
): boolean => {
  if (shouldCalculateFinalResult) {
    return true;
  }

  if (isPastWarmdown) {
    return false;
  }

  if (workoutIsPaused) {
    return false;
  }

  if (!lastMetricCalculatedTimestamp) {
    return true;
  }

  const graceTimeBeforeNextCalculation = 15 * 1000;
  const now = new Date();
  const diffSinceLastCalculation = now.getTime() - lastMetricCalculatedTimestamp.getTime();
  return diffSinceLastCalculation >= graceTimeBeforeNextCalculation;
};

const buildHeartRateData = (heartRateData: any, homeWorkoutSession: HomeWorkoutSequence) => ({
  ...heartRateData,
  timestamp: getTimeInWorkout(homeWorkoutSession, new Date()),
});

const determineIfTigernovo = (device) => {
  const tigernovoModelIds = ['TNH', 'HW'];
  return tigernovoModelIds.some((x) => device.localName
    && device.localName.startsWith(x));
};

const determineDeviceNameFromLocalname = (device) => determineIfTigernovo(device) ? 'Tigernovo monitor' : device.name;

const maskLocalName = (device) => {
  const mustMask = determineIfTigernovo(device);
  const localName = device.localName || device.name;

  if (mustMask) {
    const subStrSize = 6;
    return localName.length < subStrSize ? localName : localName.substr(localName.length - subStrSize);
  }
  return localName;
};

const hydratePreviousDataIfNecessary = (draft: IHeartRateBeltStoreState, workoutId: string) => {
  if (draft.heartRateDataPoints.length === 0) {
    const savedHeartRateData = heartRateTempPersistance.getStoredHeartRates(workoutId);
    if (savedHeartRateData.length > 0) {
      draft.heartRateDataPoints = savedHeartRateData;
    }
  }
};
